import React, { useContext, useEffect } from 'react'
import '../index.scss'
import ProfileAccountInfoSection from '@Components/ProfileAccountInfoSection'
import { StoresContext } from '@App/contexts'
import { editUserInfo } from '@App/api/routes/user'
import { UserInfo } from '@Entities/user/user'
import RootStore from '@App/stores/root-store'
import {
    userInfoFormFields,
    userInfoBaseSchema,
    userInfoSchema,
    UserInfoType,
} from '@Entities/schemas/user/user-info-schema'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import AddressSection from '@Scenes/ProfileScene/ProfileSceneUserTab/ProfileSceneUserEdit/AddressSection'
import UserInfoSection from '@Scenes/ProfileScene/ProfileSceneUserTab/ProfileSceneUserEdit/UserInfoSection'
import TopSection from '@Scenes/ProfileScene/ProfileSceneUserTab/ProfileSceneUserEdit/TopSection'
import { userinfoToZodMapper } from '@App/helpers/userinfo-to-zod-mapper'
import dayjs from 'dayjs'
import ContactInfoInterPhoneSection from '@Components/ContactInfoInterPhoneSection'
import { z } from 'zod'

interface ProfileSceneUserEditProps {
    setEditable(editable: boolean): void
    initialPhoneInvalid?: boolean
}

const normalizePhoneValue = (value?: string) => (value ?? '').replace(/\D/g, '')

// This scene only edits identity/contact/address fields. `settingsForm` and `groups` are not
// rendered here (no inputs are registered for them), so they must NOT be part of the validation
// schema below - including them causes react-hook-form/zod to treat their nested keys as
// "present but undefined", which fails validation (e.g. settingsForm.termsAndConditions:
// "invalid_type") and silently blocks handleSubmit from ever calling saveInfo(). Their original
// values are preserved separately via `profileInfo` (see cleanUpData) instead of being re-validated.
const profileSceneUserEditSchema = userInfoBaseSchema.merge(
    z.object({
        id: z.optional(z.string()),
        invitationSentTimestamp: z.optional(z.string()).nullable(),
        active: z.optional(z.boolean()),
        lastActivity: z.optional(z.string()).nullable(),
        validationStatus: z.optional(z.string()).nullable(),
        verificationStatus: z.optional(z.string()).nullable(),
        roles: z.optional(z.array(z.string())),
        isInternal: z.optional(z.boolean()),
        username: userInfoSchema.shape.username.optional(),
    })
)
type ProfileSceneUserEditType = z.infer<typeof profileSceneUserEditSchema>

const ProfileSceneUserEdit = (props: ProfileSceneUserEditProps) => {
    const { setEditable, initialPhoneInvalid } = props

    const { preloaderStore, popupStore, userStore } = useContext<RootStore>(StoresContext)

    const profileInfo: UserInfoType = userinfoToZodMapper(userStore.userInfo)

    const methods = useForm<ProfileSceneUserEditType, unknown, ProfileSceneUserEditType>({
        mode: 'onChange',
        criteriaMode: 'all',
        resolver: zodResolver(profileSceneUserEditSchema),
        defaultValues: profileInfo,
    })

    const watchedPhoneNumber = useWatch({
        control: methods.control,
        name: 'phoneNumber',
    })

    const isPhoneChanged =
        normalizePhoneValue(watchedPhoneNumber?.phoneCountryCode) !==
            normalizePhoneValue(profileInfo.phoneNumber.phoneCountryCode) ||
        normalizePhoneValue(watchedPhoneNumber?.fullPhoneNumber) !==
            normalizePhoneValue(profileInfo.phoneNumber.fullPhoneNumber)

    useEffect(
        () => () => {
            setEditable(false)
        },
        []
    )

    const { trigger } = methods

    useEffect(() => {
        if (initialPhoneInvalid) {
            trigger(['phoneNumber.phoneCountryCode', 'phoneNumber.fullPhoneNumber'])
        }
    }, [initialPhoneInvalid, trigger])

    const cleanUpData = (data: ProfileSceneUserEditType): Partial<UserInfo> => {
        const userInfo: UserInfo = {
            ...userStore.userInfo,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            birthDate: dayjs(data.birthDate).format('YYYY-MM-DD'),
            streetAddress: data.address.streetAddress,
            unitNumber: data.address.unitNumber,
            city: data.address.city,
            state: data.address.state,
            postalCode: data.address.postalCode,
            countryId: Number(data.address.countryId),
            phoneCountryCode: Number(data.phoneNumber.phoneCountryCode),
            fullPhoneNumber: data.phoneNumber.fullPhoneNumber,
        }
        // settingsForm isn't editable/rendered in this scene, so it's excluded from the validation
        // schema (see profileSceneUserEditSchema comment above). Use the original unedited value here.
        if (profileInfo.settingsForm) {
            userInfo.settings = {
                ...profileInfo.settingsForm,
                emailCommunications: userStore.userInfo?.settings?.emailCommunications ?? false,
            }
        }

        return userInfo
    }

    const saveInfo = async (data: ProfileSceneUserEditType) => {
        if (data) {
            try {
                preloaderStore.openTransparentPreloader(true)
                const editUser: Partial<UserInfo> = cleanUpData(data)
                await editUserInfo(editUser)
                await userStore.loadUserInfo()
                setEditable(false)
                preloaderStore.openTransparentPreloader(false)
                popupStore.setAlertData('Profile updated', false)
            } catch (error) {
                preloaderStore.openTransparentPreloader(false)
                popupStore.setAlertFromError(error)
            }
        }
    }

    const { firstName, lastName, birthDate, email, address, phoneNumber } = userInfoFormFields
    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(saveInfo)}>
                <div className="profile-scene-user-tab">
                    <TopSection
                        startedProfileInfo={profileInfo}
                        setNonEditable={() => setEditable(false)}
                        disabled={!methods.formState.isDirty && !isPhoneChanged}
                    />
                    <ProfileAccountInfoSection userInfo={profileInfo} />
                    <UserInfoSection firstName={firstName} lastName={lastName} birthday={birthDate} />
                    <AddressSection addressForm={address} />
                    <ContactInfoInterPhoneSection email={email} fullPhoneNumber={phoneNumber} disableEmail />
                </div>
            </form>
        </FormProvider>
    )
}

export default ProfileSceneUserEdit
